import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import { getCurrencySymbol } from '../common_components/choices';
import AsyncSelect from 'react-select/async';
import _ from 'lodash';
import ConfirmationModal from '../common_components/ConfirmationModal';

const CouponDetails = ({ show, onHide, coupon, refreshCoupons, categories: initialCategories }) => {
    const [token] = useState(localStorage.getItem('token'));
    const userInfoContent = JSON.parse(localStorage.getItem('userInfo'));
    const [formData, setFormData] = useState({
        code: '',
        description: '',
        category_id: '',
        coupon_amount: null,
        minimum_amount: null,
        currency: userInfoContent?.preferred_currency || 'XXX',
        coupon_discount: 0,
        is_custom: true,
        valid_from: '',
        valid_to: '',
        provider: '',
        status: 'active',
        notes: ''
    });

    const [shareWithCommunity, setShareWithCommunity] = useState(false);

    const isOwner = coupon ? coupon.is_owner : true;

    const [categories, setCategories] = useState(initialCategories || []);
    const [originalData, setOriginalData] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isMinAmountRequired, setIsMinAmountRequired] = useState(false);

    const handleToggleMinAmount = (e) => {
        setIsMinAmountRequired(e.target.checked);
        if (!e.target.checked) {
            setFormData(prevState => ({ ...prevState, minimum_amount: null }));
        }
    };

    useEffect(() => {
        if (coupon) {
            const initialData = {
                ...coupon,
                category_id: coupon.coupon_category.id,
                coupon_amount: coupon.coupon_amount === null ? '' : coupon.coupon_amount,
                valid_from: moment(coupon.valid_from).format('YYYY-MM-DD'),
                valid_to: moment(coupon.valid_to).format('YYYY-MM-DD')
            };
            setFormData(initialData);
            setOriginalData(initialData);
            // Enable the toggle if a minimum amount is present
            if (initialData.minimum_amount) {
                setIsMinAmountRequired(true);
            } else {
                setIsMinAmountRequired(false);
            }
        } else {
            resetFormData();
            if (categories.length === 0) {
                loadCategories();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coupon]);

    const resetFormData = () => {
        const initialData = {
            code: '',
            description: '',
            category_id: '',
            coupon_amount: null,
            minimum_amount: null,
            currency: userInfoContent?.preferred_currency || 'XXX',
            coupon_discount: 0,
            is_custom: true,
            valid_from: '',
            valid_to: '',
            provider: '',
            status: 'active',
            notes: ''
        };
        setFormData(initialData);
        setOriginalData(initialData);
    };

    // Combined function to load categories, optionally filtering by search input
    const loadCategories = async (inputValue = '', callback = null) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupon-categories/`, {
                params: { search: inputValue, page_size: 10 },
                headers: { Authorization: `Token ${token}` }
            });
            const categoriesData = response.data.results.map(category => ({
                label: category.name,
                value: category.id
            }));

            if (callback) {
                callback(categoriesData); // For AsyncSelect
            } else {
                setCategories(categoriesData); // For initial load
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            toast.error('Error loading categories');
        }
    };

    const debouncedLoadCategories = _.debounce(loadCategories, 300);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => {
            const updatedState = { ...prevState, [name]: value };
            if (name === 'coupon_amount' || name === 'minimum_amount') {
                updatedState.currency = value ? userInfoContent?.preferred_currency || 'XXX' : '';
            }
            return updatedState;
        });
    };

    const handleToggleChange = (e) => {
        setShareWithCommunity(e.target.checked);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const method = coupon ? 'patch' : 'post';
            const url = coupon
                ? `${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupons/${coupon.id}/`
                : `${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupons/`;

            const payload = Object.keys(formData).reduce((acc, key) => {
                if (formData[key] !== originalData[key]) {
                    acc[key] = formData[key];
                }
                return acc;
            }, {});

            if (payload.coupon_amount || payload.minimum_amount) {
                payload.currency = formData.currency || userInfoContent?.preferred_currency || 'XXX';
            }

            if (payload.coupon_amount === '') {
                payload.coupon_amount = null;
            }

            if (payload.minimum_amount === '') {
                payload.minimum_amount = null;
            }

            if (payload.coupon_amount === null) {
                payload.currency = "";
            }

            payload.is_custom = !shareWithCommunity;

            if (payload.valid_from) {
                payload.valid_from = moment(payload.valid_from).format('YYYY-MM-DD');
            }
            if (payload.valid_to) {
                payload.valid_to = moment(payload.valid_to).format('YYYY-MM-DD');
            }

            console.log('Payload:', JSON.stringify(payload, null, 2));

            await axios[method](url, payload, {
                headers: {
                    Authorization: `Token ${token}`,
                }
            });

            toast.success(`Coupon ${coupon ? 'updated' : 'created'} successfully`);
            refreshCoupons();
            onHide();
        } catch (error) {
            console.error('Error saving coupon:', error);
            toast.error('Error saving coupon');
        }
    };

    const handleDelete = async () => {
        if (!coupon || !coupon.id) {
            toast.error('Cannot delete coupon. Invalid coupon.');
            return;
        }

        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupons/${coupon.id}/`, {
                headers: { Authorization: `Token ${token}` },
            });

            toast.success('Coupon deleted successfully');
            refreshCoupons();
            onHide();
        } catch (error) {
            console.error('Error deleting coupon:', error);
            toast.error('Error deleting coupon');
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleCopyCode = () => {
        navigator.clipboard.writeText(formData.code);
        toast.info('Coupon code copied to clipboard');
    };

    const handleCopyCouponDetails = async () => {
        try {
            const payload = { ...formData };

            if (payload.coupon_amount === '') {
                payload.coupon_amount = null;
            }
            payload.is_custom = false;

            delete payload.id;
            delete payload.coupon_category;
            delete payload.is_owner;
            delete payload.is_verified;

            const url = `${process.env.REACT_APP_BACKEND_API_URL}/astapor/coupons/`;
            await axios.post(url, payload, {
                headers: {
                    Authorization: `Token ${token}`,
                }
            });

            toast.success("Coupon details copied. Please check the new coupon in the 'My Coupons' section.");
            refreshCoupons();
            onHide();
        } catch (error) {
            console.error('Error copying coupon details:', error);
            toast.error('Error copying coupon details');
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {coupon ? (isOwner ? 'Edit Coupon' : 'View Coupon') : 'Add Coupon'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group controlId="couponCode">
                                <Form.Label>Coupon Code</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleChange}
                                        required
                                        disabled={!isOwner}
                                    />
                                    {!isOwner && (
                                        <InputGroup.Text onClick={() => handleCopyCode()} style={{ cursor: 'pointer' }}>
                                            <i className="bi bi-clipboard"></i>
                                        </InputGroup.Text>
                                    )}
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="provider">
                                <Form.Label>Provider</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="provider"
                                    value={formData.provider}
                                    onChange={handleChange}
                                    required
                                    disabled={!isOwner}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group controlId="description">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            disabled={!isOwner}
                        />
                    </Form.Group>
                    <Row>
                        <Col>
                            <Form.Group controlId="category">
                                <Form.Label>Category</Form.Label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions={categories}
                                    loadOptions={debouncedLoadCategories}
                                    onChange={(selected) => handleChange({ target: { name: 'category_id', value: selected.value } })}
                                    value={categories.find(cat => cat.value === formData.category_id) || null}
                                    isDisabled={!isOwner}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col md={6}>
                            <Form.Group controlId="minAmountRequired">
                                <Form.Check
                                    type="switch"
                                    label="Purchase Required"
                                    checked={isMinAmountRequired}
                                    onChange={handleToggleMinAmount}
                                />
                            </Form.Group>
                        </Col>

                        {isMinAmountRequired && (
                            <Col md={6}>
                                <Form.Group controlId="minimumAmount">
                                    <Form.Label>Minimum Amount</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>{getCurrencySymbol(userInfoContent.preferred_currency)}</InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            name="minimum_amount"
                                            value={formData.minimum_amount || ''}
                                            onChange={handleChange}
                                            placeholder="Enter minimum amount"
                                            step="1"
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        )}
                    </Row>
                    <Row className='mt-3'>
                        <Col md={6}>
                            <Form.Group controlId="couponDiscount">
                                <Form.Label>Coupon Discount</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        name="coupon_discount"
                                        value={formData.coupon_discount}
                                        onChange={handleChange}
                                        required
                                        disabled={!isOwner}
                                    />
                                    <InputGroup.Text>%</InputGroup.Text>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="couponAmount">
                                <Form.Label>Maximum Coupon Amount</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>{getCurrencySymbol(userInfoContent.preferred_currency)}</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        name="coupon_amount"
                                        value={formData.coupon_amount}
                                        onChange={handleChange}
                                        placeholder={formData.coupon_amount === '' ? 'Enter amount' : ''}
                                        disabled={!isOwner}
                                        step="1"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className='mt-3'>
                        <Col md={6}>
                            <Form.Group controlId="validFrom">
                                <Form.Label>Valid From</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="valid_from"
                                    value={formData.valid_from}
                                    onChange={handleChange}
                                    required
                                    disabled={!isOwner}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="validTo">
                                <Form.Label>Valid To</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="valid_to"
                                    value={formData.valid_to}
                                    onChange={handleChange}
                                    required
                                    disabled={!isOwner}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    {isOwner && (
                        <>
                            <Row className='mt-3'>
                                <Form.Group controlId="notes">
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        disabled={!isOwner}
                                    />
                                </Form.Group>
                            </Row>
                            <Row className='mt-3'>
                                <Form.Group controlId="shareWithCommunity">
                                    <Form.Check
                                        type="switch"
                                        label="Share this coupon with the community"
                                        checked={shareWithCommunity}
                                        onChange={handleToggleChange}
                                        disabled={!isOwner}
                                    />
                                </Form.Group>
                            </Row>
                            <Row className='mt-3'>
                                <Form.Group controlId="status">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        required
                                        disabled={!isOwner}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="used">Used</option>
                                    </Form.Control>
                                </Form.Group>
                            </Row>
                        </>
                    )}
                    <Row className="mt-3">
                        {isOwner && (
                            <Col xs="auto">
                                <Button variant="primary" type="submit">
                                    {coupon ? 'Update Coupon' : 'Create Coupon'}
                                </Button>
                            </Col>
                        )}
                        {isOwner && coupon && (
                            <Col xs="auto">
                                <Button variant="danger" onClick={() => setShowConfirmModal(true)} className="ml-3">
                                    Delete Coupon
                                </Button>
                            </Col>
                        )}
                        {!isOwner && (
                            <Col xs="auto">
                                <Button variant="success" onClick={handleCopyCouponDetails} className="ml-3">
                                    Copy Coupon Details
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Form>
                <ConfirmationModal
                    show={showConfirmModal}
                    onHide={() => setShowConfirmModal(false)}
                    onConfirm={handleDelete}
                    title="Confirm Deletion"
                    body="Are you sure you want to delete this coupon? This action cannot be undone."
                />
            </Modal.Body>
        </Modal>
    );
};

export default CouponDetails;
